import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Service metadata labels and colors for admin dashboard display
 */
export const SERVICE_META = {
  businesses: { label: 'Businesses', color: '#2563eb', icon: 'Store' },
  busSchedules: { label: 'Bus Timetable', color: '#059669', icon: 'Bus' },
  contacts: { label: 'Important Contacts', color: '#d97706', icon: 'Phone' },
  marketplaceListings: { label: 'Marketplace (Buy & Sell)', color: '#7c3aed', icon: 'Tag' },
  events: { label: 'Events Gallery', color: '#e11d48', icon: 'Calendar' },
  places: { label: 'Memorable Places', color: '#0d9488', icon: 'MapPin' },
  announcements: { label: 'Announcements', color: '#dc2626', icon: 'Megaphone' },
  apmcMarkets: { label: 'APMC Market Prices', color: '#16a34a', icon: 'TrendingUp' },
  village: { label: 'About Village', color: '#4b5563', icon: 'Info' },
  managers: { label: 'Managers & Leaders', color: '#6366f1', icon: 'Users' },
  developers: { label: 'Developers Info', color: '#0891b2', icon: 'Code' },
};

/**
 * Fetch lifetime overview counters (1 read)
 */
export async function fetchAnalyticsOverview() {
  try {
    const overviewRef = doc(db, 'analytics_summary', 'overview');
    const snap = await getDoc(overviewRef);
    if (!snap.exists()) {
      return {
        totalServiceOpens: 0,
        services: {},
        lastUpdated: null,
      };
    }
    return snap.data();
  } catch (err) {
    console.error('Failed to fetch analytics overview:', err);
    return { totalServiceOpens: 0, services: {}, lastUpdated: null };
  }
}

/**
 * Fetch currently active users (active within the last 5 minutes)
 */
export async function fetchCurrentlyActiveCount() {
  try {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    const presenceRef = collection(db, 'analytics_presence');
    
    // Low-cost presence query
    const snap = await getDocs(presenceRef);
    let activeCount = 0;
    const activeDevices = [];

    snap.forEach((docSnap) => {
      const data = docSnap.data();
      const lastActiveTime = data.lastActive?.toDate ? data.lastActive.toDate() : (data.lastActive ? new Date(data.lastActive) : null);
      if (lastActiveTime && lastActiveTime >= fiveMinutesAgo) {
        activeCount += 1;
        activeDevices.push({ id: docSnap.id, ...data, lastActiveTime });
      }
    });

    return { activeCount, activeDevices };
  } catch (err) {
    console.error('Failed to fetch currently active users:', err);
    return { activeCount: 0, activeDevices: [] };
  }
}

/**
 * Fetch daily stats history (e.g. past 14 days)
 */
export async function fetchDailyAnalytics(limitDays = 14) {
  try {
    const dailyRef = collection(db, 'analytics_daily');
    const q = query(dailyRef, orderBy('date', 'desc'), limit(limitDays));
    const snap = await getDocs(q);

    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
  } catch (err) {
    console.error('Failed to fetch daily analytics:', err);
    return [];
  }
}

/**
 * Fetch top viewed items for a specific module (Businesses, Events, Places)
 */
export async function fetchTopItems(moduleName, limitCount = 10) {
  try {
    const viewsRef = collection(db, 'analytics_item_views');
    
    // First try with composite index
    try {
      const q = query(
        viewsRef,
        where('module', '==', moduleName),
        orderBy('views', 'desc'),
        limit(limitCount),
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch {
      // Fallback in case index is still propagating
      const fallbackQ = query(viewsRef, where('module', '==', moduleName));
      const snap = await getDocs(fallbackQ);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.views || 0) - (a.views || 0));
      return items.slice(0, limitCount);
    }
  } catch (err) {
    console.error(`Failed to fetch top ${moduleName}:`, err);
    return [];
  }
}

/**
 * Aggregate all analytics for Admin Analytics Dashboard
 */
export async function fetchFullAnalytics() {
  const [overview, presence, daily, topBusinesses, topEvents, topPlaces] = await Promise.all([
    fetchAnalyticsOverview(),
    fetchCurrentlyActiveCount(),
    fetchDailyAnalytics(14),
    fetchTopItems('businesses', 10),
    fetchTopItems('events', 10),
    fetchTopItems('places', 10),
  ]);

  // Extract today's DAU
  const todayStr = new Date().toISOString().slice(0, 10);
  const todayRecord = daily.find((d) => d.date === todayStr || d.id === todayStr);
  const todayActiveUsers = todayRecord?.activeUsers || 0;

  // Platform breakdown for active devices
  const platforms = { android: 0, ios: 0, other: 0 };
  (presence.activeDevices || []).forEach((dev) => {
    const p = String(dev.platform || '').toLowerCase();
    if (p.includes('android')) platforms.android += 1;
    else if (p.includes('ios')) platforms.ios += 1;
    else platforms.other += 1;
  });

  return {
    overview,
    currentlyActive: presence.activeCount,
    activeDevices: presence.activeDevices,
    platforms,
    todayActiveUsers,
    daily,
    topBusinesses,
    topEvents,
    topPlaces,
  };
}
